export type AnalyticsField =
  | 'ga_measurement_id'
  | 'gtm_id'
  | 'facebook_pixel_id'
  | 'recaptcha_site_key';

export type AnalyticsConsentMode = 'implicit' | 'opt_in' | 'opt_out';

type AnalyticsMeta = {
  pattern: RegExp;
  uppercase: boolean;
  example: string;
  message: string;
  maxLength?: number;
};

export const ANALYTICS_FIELD_META: Record<AnalyticsField, AnalyticsMeta> = {
  ga_measurement_id: {
    pattern: /^G-[A-Z0-9]{10}$/,
    uppercase: true,
    example: 'G-ABC123DEF4',
    message: 'Usá el formato GA4 G-XXXXXXXXXX (solo mayúsculas y números).',
    maxLength: 12,
  },
  gtm_id: {
    pattern: /^GTM-[A-Z0-9]{6,8}$/,
    uppercase: true,
    example: 'GTM-ABC1234',
    message: 'Usá el formato GTM-XXXXXXX (solo mayúsculas y números).',
    maxLength: 12,
  },
  facebook_pixel_id: {
    pattern: /^[0-9]{5,20}$/,
    uppercase: false,
    example: '123456789012345',
    message: 'El Pixel debe tener entre 5 y 20 dígitos.',
    maxLength: 20,
  },
  recaptcha_site_key: {
    pattern: /^[A-Za-z0-9_-]{30,60}$/,
    uppercase: false,
    example: '6LcExampleSiteKeyValue1234567890',
    message: 'La site key debe tener 30 a 60 caracteres alfanuméricos.',
    maxLength: 60,
  },
};

export function formatAnalyticsValue(field: AnalyticsField, value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const meta = ANALYTICS_FIELD_META[field];
  return meta.uppercase ? trimmed.toUpperCase() : trimmed;
}

export function isAnalyticsValueValid(field: AnalyticsField, value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value !== 'string') {
    return false;
  }
  const formatted = formatAnalyticsValue(field, value);
  if (!formatted) {
    return value.trim().length === 0;
  }
  return ANALYTICS_FIELD_META[field].pattern.test(formatted);
}

export const ANALYTICS_CONSENT_MODE_OPTIONS: Array<{
  value: AnalyticsConsentMode;
  label: string;
  description: string;
}> = [
  {
    value: 'implicit',
    label: 'Implícito',
    description: 'Dispara analytics/marketing salvo opt-out explícito.',
  },
  {
    value: 'opt_out',
    label: 'Opt-out',
    description: 'Analytics habilitado por defecto; marketing requiere consentimiento.',
  },
  {
    value: 'opt_in',
    label: 'Opt-in',
    description: 'No se exporta nada hasta recibir consentimiento explícito.',
  },
];

import { createAnalyticsClient } from '@go-analytics/web-sdk';

export let analytics: ReturnType<typeof createAnalyticsClient> | null = null;

export const initAnalytics = (token: string | null | undefined) => {
  if (!token) return;
  if (analytics) return; // Ya inicializado

  const endpoint = import.meta.env.VITE_GO_ANALYTICS_EVENTS_ENDPOINT;
  if (!endpoint) {
    console.warn('Go Analytics: VITE_GO_ANALYTICS_EVENTS_ENDPOINT is not defined');
    return;
  }

  analytics = createAnalyticsClient({
    token: token,
    endpoint: endpoint,
    flushIntervalMs: 5000,
    batchSize: 10,
  });
};

